import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import logger from '../utils/logger';

// Firebase configuration - using parent domain for cross-subdomain auth
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  // CROSS-DOMAIN AUTH: Use parent domain from .env (jugglevision.ai)
  // This enables auth session sharing across all jugglevision.ai subdomains
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
logger.log('🔥 Initializing Firebase with config:', firebaseConfig);

let app: any, db: any, auth: any;

try {
  // Initialize Firebase app
  app = initializeApp(firebaseConfig);
  db = getFirestore(app);

  // Initialize Auth with proper configuration for cross-domain support
  auth = getAuth(app);

  // Import and configure persistence immediately
  (async () => {
    try {
      // Import auth types
      const { browserSessionPersistence, setPersistence } = await import('firebase/auth');

      // Set persistence to SESSION to avoid cross-domain conflicts
      // We'll rely on session cookies for cross-domain auth instead
      logger.log('🔥 Setting Firebase Auth persistence to browserSessionPersistence');
      await setPersistence(auth, browserSessionPersistence);

      // Log auth configuration for debugging
      logger.log('✅ Firebase Auth persistence set successfully');
      logger.log('✅ Auth domain:', auth.app.options.authDomain);
      logger.log('✅ Current origin:', window.location.origin);

      // Force cookies to be set for the parent domain
      // This is critical for cross-domain auth to work
      (auth as any)._canUseEmulator = false; // Prevent emulator connection in production
      (auth as any)._parentDomain = 'jugglevision.ai';

      // Log current auth state
      logger.log('✅ Initial auth state:', auth.currentUser ?
        `Signed in as ${auth.currentUser.email}` : 'Not signed in');
    } catch (error) {
      logger.error('❌ Error configuring Firebase Auth:', error);
    }
  })();

  if (process.env.NODE_ENV !== 'production') {
    (window as any).firebaseConfig = firebaseConfig;

    (window as any).testGoogleSignIn = async () => {
      try {
        logger.log('🔐 Starting Google sign-in test...');
        const { signInWithPopup, GoogleAuthProvider } = await import('firebase/auth');
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        logger.log('✅ Google sign-in SUCCESS:', result.user.email);
        logger.log('✅ User details:', result.user);
        return result;
      } catch (error: any) {
        logger.error('❌ Google sign-in FAILED:', error.code, error.message);
        logger.error('❌ Full error:', error);
        throw error;
      }
    };
  }

  logger.log('🔥 Firebase initialized successfully');
  logger.log('🔥 Auth object:', auth);
  logger.log('🔥 Auth domain:', auth.app.options.authDomain);

  // Test auth state immediately
  auth.onAuthStateChanged((user: any) => {
    logger.log('🔥 Initial auth state check:', user ? `Logged in as ${user.email}` : 'Not logged in');
  });

} catch (error) {
  logger.error('🔥 Firebase initialization failed:', error);
  logger.error('🔥 Firebase config was:', firebaseConfig);
  
  // Don't throw the error in development, just log it
  if (process.env.NODE_ENV === 'production') {
    throw error;
  }
}

export { app, db, auth };
